import * as React from "react";
import styled, { css } from "styled-components";

import { createEntitiesConsumer } from "../../../lib/entities";
import { media, lato } from "../../../common/utils/style";
import { Company, CompanyValue as _CompanyValue, Image } from "../../entities";
import ImageComponent from "../../../common/components/Image";
import { FacebookButtonRound, TwitterButtonRound } from "../../../common/components/sns";
import { openFacebookShareWindow, openTwitterShareWindow } from "../../../common/utils/sns";

type CompanyValue = Pick<_CompanyValue, "feature_title" | "feature_cd" | "feature_color" | "rank">;

interface OwnProps {
  company: Company;
  selectedFeatures: number[];
  switchDisabled?: boolean;
  className?: string;
}

type Props = OwnProps;

interface State {
  isHovered: boolean;
  isTouched: boolean;
}

const ImageEntitiesConsumer = createEntitiesConsumer<{ image: Image }>({ image: "images" });

const ValueEntitiesConsumer = createEntitiesConsumer<{ values: CompanyValue[] }>({ values: "company_values" });

class CompanyCard extends React.Component<Props, State> {
  private isCalledOnTouchMove?: boolean;

  constructor(props: Props) {
    super(props);
    this.state = {
      isHovered: false,
      isTouched: false,
    };
  }

  public onTouchEndLink = (event: React.TouchEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  public onTouchMove = () => {
    this.isCalledOnTouchMove = true;
  };

  public onTouchEnd = () => {
    if (!this.isCalledOnTouchMove) {
      this.setState({ isTouched: !this.state.isTouched, isHovered: false });
    }
    this.isCalledOnTouchMove = undefined;
  };

  public onMouseEnter = () => {
    this.setState({
      isHovered: true,
    });
  };

  public onMouseLeave = () => {
    this.setState({
      isHovered: false,
    });
  };

  public onClickToShare = (target: "facebook" | "twitter") => {
    const url = `https://www.wantedly.com/values?company_id=${this.props.company.id}`;
    if (target === "facebook") {
      openFacebookShareWindow({ url });
    } else {
      openTwitterShareWindow({ url });
    }
    (window as any).logEvent("values_lp:company_card_share_button", "click", { target });
  };

  public render() {
    const { company } = this.props;

    return (
      <ValueEntitiesConsumer query={{ values: company.visible_company_values }}>
        {({ values }) => {
          return values ? this.renderWithValues({ values }) : null;
        }}
      </ValueEntitiesConsumer>
    );
  }

  public renderWithValues({ values }: { values: CompanyValue[] }) {
    const { company, switchDisabled, selectedFeatures, className } = this.props;
    const sortedValues = values.sort((a, b) => a.rank - b.rank);
    const matchedValues = sortedValues.filter(val => selectedFeatures.includes(val.feature_cd));
    const notMatchedValues = sortedValues.filter(val => !selectedFeatures.includes(val.feature_cd));
    const { isHovered, isTouched } = this.state;
    const isSwitched = !switchDisabled && (isHovered || isTouched);
    return (
      <Base
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        onTouchMove={this.onTouchMove}
        onTouchEnd={this.onTouchEnd}
        switchDisabled={switchDisabled}
        className={className}
      >
        <CoverImageWrapper>
          {company.background && (
            <ImageEntitiesConsumer query={{ image: company.background }}>
              {({ image }) =>
                image && <CompanyCoverImage image={image} size="342x100#" alt={company.name} background />
              }
            </ImageEntitiesConsumer>
          )}
        </CoverImageWrapper>
        <AvatarWrapper>
          <ImageEntitiesConsumer query={{ image: company.avatar }}>
            {({ image }) => image && <Avatar image={image} size="100x100m" alt="company_avatar" />}
          </ImageEntitiesConsumer>
        </AvatarWrapper>
        {!switchDisabled &&
          matchedValues.length >= 3 && (
            <MatchedTag>
              <MatchedNumber>{matchedValues.length}</MatchedNumber>
              <MatchedDesc>Match</MatchedDesc>
            </MatchedTag>
          )}
        <CompanyWrapper>
          {isSwitched ? (
            <>
              <HeaderTitle>価値観と一緒にこの会社をシェア</HeaderTitle>
              <ShareButtonWrapper>
                <FacebookButton onClick={() => this.onClickToShare("facebook")} onTouchEnd={this.onTouchEndLink} />
                <TwitterButton onClick={() => this.onClickToShare("twitter")} onTouchEnd={this.onTouchEndLink} />
              </ShareButtonWrapper>
              <CompanyPageButton href={company.company_path} target="_blank" onTouchEnd={this.onTouchEndLink}>
                会社ページを見る
              </CompanyPageButton>
            </>
          ) : (
            <>
              <HeaderTitle>{company.name}</HeaderTitle>
              <ValueWrapper>
                <ValueList>
                  {matchedValues.map(value => (
                    <ValueLabel key={value.feature_cd} color={value.feature_color} selected>
                      {value.feature_title}
                    </ValueLabel>
                  ))}
                  {notMatchedValues.map(value => (
                    <ValueLabel key={value.feature_cd} color={value.feature_color} switchDisabled={switchDisabled}>
                      {value.feature_title}
                    </ValueLabel>
                  ))}
                </ValueList>
              </ValueWrapper>
            </>
          )}
        </CompanyWrapper>
      </Base>
    );
  }
}

export default CompanyCard;

const Base = styled.div`
  position: relative;
  overflow: hidden;
  margin: 0 auto;
  max-width: 343px;
  min-height: 366px;
  @media ${media.large} {
    max-width: 308px;
    width: 308px;
    margin: 10px 0 18px;
  }
  background-color: white;
  border-radius: 8px;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.08);
  transform: translateY(0);
  transition: transform 200ms ease-out, box-shadow 200ms ease-out;
  &:focus {
    outline: none;
  }
  ${(props: { switchDisabled?: boolean }) => {
    return props.switchDisabled
      ? css`
          flex-shrink: 0;
        `
      : css`
          &:hover {
            transform: translateY(-7px);
            box-shadow: 0 12px 24px 0 rgba(0, 0, 0, 0.1);
            text-decoration: none;
          }
        `;
  }};
`;

const MatchedTag = styled.div`
  position: absolute;
  top: 0px;
  left: 16px;
  width: 50px;
  height: 56px;
  background-color: rgba(9, 197, 231, 1);
  color: rgba(255, 255, 255, 1);
  ${lato};
  font-weight: 600;
  text-align: center;
`;

const MatchedNumber = styled.div`
  margin-top: 2px;
  font-size: 30px;
  line-height: 36px;
`;

const MatchedDesc = styled.div`
  font-size: 12px;
  line-height: 1;
`;

const CoverImageWrapper = styled.div`
  height: 100px;
  background-color: #bbb;
`;

const CompanyCoverImage = styled(ImageComponent)`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
`;

const CompanyWrapper = styled.div`
  padding: 0 16px;
  margin: 0 auto;
`;

const HeaderTitle = styled.div`
  margin-top: 62px;
  font-weight: 700;
  font-size: 18px;
  line-height: 1.22;
  text-align: center;
  color: rgba(0, 0, 0, 0.8);
`;

const AvatarWrapper = styled.div`
  position: absolute;
  right: calc(50% - 50px);
  top: 50px;
  overflow: hidden;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 1);
`;

const Avatar = styled(ImageComponent)`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: solid 4px white;
  box-sizing: border-box;
`;

const ValueWrapper = styled.div`
  padding: 16px 0 24px;
`;

const ValueList = styled.div`
  margin-top: 11px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
  @media ${media.large} {
    justify-content: center;
  }
`;

const ValueLabel = styled.div`
  margin: 5px;
  height: 26px;
  line-height: 26px;
  font-weight: 500;
  font-size: 12px;
  text-align: center;
  border-radius: 100px;
  background-color: rgba(97, 143, 235, 1);
  color: rgba(255, 255, 255, 1);
  padding: 0 8px;
  ${(p: { selected?: boolean; color: string; switchDisabled?: boolean }) =>
    p.switchDisabled || p.selected
      ? css`
          background-color: ${p.color};
        `
      : css`
          background-color: rgba(0, 0, 0, 0.12);
        `};
`;

const ShareButtonWrapper = styled.div`
  margin: 16px auto 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ShareButtonBase = css`
  width: 50px;
  height: 50px;
  font-size: 25px;
  @media ${media.large} {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
`;

const FacebookButton = styled(FacebookButtonRound)`
  ${ShareButtonBase};
  margin-right: 18px;
`;

const TwitterButton = styled(TwitterButtonRound)`
  ${ShareButtonBase};
`;

const CompanyPageButton = styled.a`
  margin: 50px auto 0;
  display: block;
  width: 276px;
  height: 44px;
  line-height: 44px;
  border-radius: 25px;
  font-weight: 400;
  font-size: 14px;
  text-align: center;
  color: rgba(0, 0, 0, 0.56) !important;
  transition: background-color 0.2s;
  background-color: white;
  border: solid 1px rgba(0, 0, 0, 0.27);
  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
    text-decoration: none;
  }
  &:focus {
    outline: none;
  }
`;



// WEBPACK FOOTER //
// ./frontend/src/company_values/components/campaign/CompanyCard.tsx